import React, { useState } from "react";

import {
  Button,
  Col,
  Collapse,
  Divider,
  Row,
  Space,
  Spin,
  Table,
  notification,
  Modal,
  Card,
  Typography,
  Empty,
} from "antd";
import {
  EditOutlined,
  NotificationOutlined,
  PlusOutlined,
  UnorderedListOutlined,
  PoweroffOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { API_URL } from "../../shared/utils/Util";
import { getFetchT } from "../../shared/utils/TypedUtil";
import { GetAnnouncementsResponse } from "../../types/api";
import { Announcement } from "../../types/model";
import { useAntdTheme } from "../../shared/hooks";

import { NewAnnouncementForm } from "./Form";
import { AnnouncementDeleteButton } from "./DeleteButton";
import { AnnouncementShutdownButton } from "./ShutdownButton";

const { Panel } = Collapse;
const { Title, Text } = Typography;

type AnnouncementEditorProps = {
  restaurantId: string;
};

export const AnnouncementEditor = (props: AnnouncementEditorProps) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const [announcements, setAnnouncements] = React.useState<Announcement[] | undefined>(undefined);

  const refreshAnnouncements = () => {
    getFetchT<GetAnnouncementsResponse>(
      `${API_URL}/v1/public/r/${props.restaurantId}/announcements`,
      (result: GetAnnouncementsResponse) => {
        setAnnouncements(result.announcements);
      },
      (err) => {
        notification.error({
          message: t("error"),
          description: t("error_occurred"),
        });
      }
    );
  };

  // Load current set of announcemets.
  React.useEffect(refreshAnnouncements, [props.restaurantId]);

  if (announcements === undefined) {
    // Still loading
    return <Spin size="large" />;
  }

  const onChange = () => {
    // Set announcements to undefined to show loading icon again.
    setAnnouncements(undefined);
    refreshAnnouncements();
  };

  const columns: any = [
    {
      title: t("title"),
      dataIndex: "title",
      defaultSortOrder: "ascend",
      sortDirections: ["ascend", "descend"],
      sorter: (a: Announcement, b: Announcement) => {
        return a.title.localeCompare(b.title);
      },
    },
    {
      title: t("content"),
      dataIndex: "content",
    },
    {
      title: t("type"),
      dataIndex: "annoucement_type",
    },
    {
      render: (text: string, record: Announcement) => (
        <AnnouncementDeleteButton
          restaurantId={props.restaurantId}
          announcement={record}
          onSuccess={onChange}
        />
      ),
    },
  ];

  let alreadyShutdown = false;
  let existingShutdownId = undefined;
  for (const announcement of announcements ?? []) {
    if (announcement.annoucement_type === "SHUT_DOWN") {
      alreadyShutdown = true;
      existingShutdownId = announcement.id;
      break;
    }
  }

  return (
    <div>
      {/* Shutdown Control */}
      <Card
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `2px solid ${alreadyShutdown ? token.colorError : token.colorBorder}`,
          background: alreadyShutdown ? `${token.colorError}10` : token.colorBgContainer,
          marginBottom: token.marginXL,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        <Space direction="vertical" style={{ width: "100%", gap: token.marginMD }}>
          <Space>
            <PoweroffOutlined
              style={{
                fontSize: 20,
                color: alreadyShutdown ? token.colorError : token.colorTextSecondary,
              }}
            />
            <Title level={4} style={{ margin: 0 }}>
              {t("system_shutdown_control")}
            </Title>
          </Space>
          <AnnouncementShutdownButton
            restaurantId={props.restaurantId}
            alreadyShutdown={alreadyShutdown}
            existingShutdownId={existingShutdownId}
            onSuccess={onChange}
          />
        </Space>
      </Card>

      {/* Create New Announcement */}
      <Card
        title={
          <Space>
            <PlusOutlined style={{ fontSize: 20, color: token.colorSuccess }} />
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {t("create_new_announcement")}
            </Text>
          </Space>
        }
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
          marginBottom: token.marginXL,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        <NewAnnouncementForm restaurantId={props.restaurantId} onSuccess={onChange} />
      </Card>

      {/* Current Announcements */}
      <Card
        title={
          <Space>
            <UnorderedListOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {t("current_announcements")}
            </Text>
          </Space>
        }
        style={{
          borderRadius: token.borderRadiusLG,
          border: `1px solid ${token.colorBorder}`,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        {!announcements || announcements.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Text type="secondary">{t("no_announcements")}</Text>}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={announcements}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `${t("total")} ${total} ${t("items")}`,
            }}
            style={{ borderRadius: token.borderRadiusLG }}
          />
        )}
      </Card>
    </div>
  );
};
